import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Percent, Loader2, AlertCircle, CheckCircle2, FileSpreadsheet, Plus } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n, c = 'PHP') => new Intl.NumberFormat('en-PH', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n || 0);

export default function FinanceTaxPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [summary, setSummary] = useState(null);
  const [taxCats, setTaxCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(null);  // null closed, {} new, {..} editing

  const saveCat = async () => {
    if (!editOpen?.code?.trim() || !editOpen?.name?.trim()) {
      toast.error('Code and name are required');
      return;
    }
    const body = {
      code: editOpen.code.trim().toUpperCase(),
      name: editOpen.name.trim(),
      rate: Number(editOpen.rate) || 0,
      country: (editOpen.country || '').toUpperCase(),
      type: editOpen.type || 'vat',
    };
    try {
      // If editing existing (had a persisted id / created_at), use PATCH
      if (editOpen.id || editOpen.created_at) {
        await axios.patch(`${API}/api/finance/tax-categories/${body.code}`, body);
      } else {
        await axios.post(`${API}/api/finance/tax-categories`, body);
      }
      toast.success('Tax category saved');
      setEditOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteCat = async (code) => {
    if (!window.confirm(`Delete tax category ${code}?`)) return;
    try {
      await axios.delete(`${API}/api/finance/tax-categories/${code}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: setup }] = await Promise.all([
        axios.get(`${API}/api/finance/reports/tax-summary`, { params: { start, end } }),
        axios.get(`${API}/api/finance/setup`),
      ]);
      setSummary(t);
      setTaxCats(setup.tax_categories || []);
    } catch {
      toast.error('Failed to load tax data');
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const exportFiling = () => {
    if (!summary) return;
    const csv = [
      ['VAT/Tax filing report', `${start} to ${end}`],
      [],
      ['Output tax (collected)', summary.output_tax],
      ['Output taxable base', summary.output_taxable_base],
      [],
      ['Input tax (paid)', summary.input_tax],
      ['Input taxable base', summary.input_taxable_base],
      [],
      ['Net tax payable', summary.net_tax_payable],
      [],
      ['By category'],
      ['Category', 'Input', 'Output'],
      ...Object.entries(summary.by_category || {}).map(([k, v]) => [k, v.input, v.output]),
    ].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `tax-filing-${start}-${end}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Filing exported');
  };

  return (
    <div className="page-shell-wide" data-testid="finance-tax-page">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            <Percent className="h-3 w-3" /> Finance · Tax & VAT
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Tax &amp; VAT<PageInfoTooltip pageKey="finance-tax" /></h1>
          <p className="text-sm text-gray-600 mt-1">Output tax collected, input tax paid, and what you owe the tax authority.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-[10px]">From</Label><Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-9 w-36" data-testid="tax-start" /></div>
          <div><Label className="text-[10px]">To</Label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-9 w-36" data-testid="tax-end" /></div>
          <Button variant="outline" size="sm" onClick={exportFiling} data-testid="export-tax-btn"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Export filing</Button>
        </div>
      </div>

      {loading || !summary ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Output tax (collected)</div>
                <div className="text-2xl font-bold text-emerald-900 mt-1">{fmt(summary.output_tax)}</div>
                <div className="text-xs text-emerald-700 mt-1">on {fmt(summary.output_taxable_base)} of taxable sales</div>
              </CardContent>
            </Card>
            <Card className="border-sky-200 bg-sky-50">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-wider text-sky-700">Input tax (paid to vendors)</div>
                <div className="text-2xl font-bold text-sky-900 mt-1">{fmt(summary.input_tax)}</div>
                <div className="text-xs text-sky-700 mt-1">on {fmt(summary.input_taxable_base)} of purchases</div>
              </CardContent>
            </Card>
            <Card className={summary.net_tax_payable >= 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}>
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-wider text-gray-700">{summary.net_tax_payable >= 0 ? 'Net tax payable' : 'Net tax refundable'}</div>
                <div className="text-2xl font-bold mt-1 text-gray-900">{fmt(Math.abs(summary.net_tax_payable))}</div>
                <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                  {summary.net_tax_payable >= 0
                    ? <><AlertCircle className="h-3 w-3" /> Set this aside — due to tax authority</>
                    : <><CheckCircle2 className="h-3 w-3" /> Eligible for refund / carry-forward</>}
                </div>
              </CardContent>
            </Card>
          </div>

          {Object.keys(summary.by_category || {}).length > 0 && (
            <Card className="mb-5">
              <CardHeader><CardTitle className="text-base">By tax category</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase text-gray-500">
                    <tr><th className="text-left p-2">Category</th><th className="text-right p-2">Input</th><th className="text-right p-2">Output</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(summary.by_category).map(([cat, v]) => (
                      <tr key={cat} className="border-t border-gray-100">
                        <td className="p-2 font-mono text-xs">{cat}</td>
                        <td className="p-2 text-right font-mono">{fmt(v.input)}</td>
                        <td className="p-2 text-right font-mono">{fmt(v.output)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Configured tax categories</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setEditOpen({})} data-testid="tax-cat-add-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add tax rule
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {taxCats.map(t => (
                  <div key={t.code} className="p-3 rounded-md border border-gray-200 hover:border-emerald-300 transition-colors group relative" data-testid={`tax-cat-${t.code}`}>
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 pr-2">
                        <div className="text-sm font-semibold truncate">{t.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5 font-mono">{t.code}{t.country ? ` · ${t.country}` : ''}</div>
                      </div>
                      <div className="text-xl font-bold text-gray-900">{(t.rate * 100).toFixed(t.rate * 100 >= 10 ? 0 : 1)}%</div>
                    </div>
                    <div className="mt-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditOpen({ ...t })} data-testid={`tax-cat-edit-${t.code}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-500 hover:text-red-600" onClick={() => deleteCat(t.code)} data-testid={`tax-cat-delete-${t.code}`}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">Tax categories ship pre-configured for PH, AE, AU, IN. Add custom jurisdictions per country as needed.</p>
            </CardContent>
          </Card>
        </>
      )}

      {/* ─── Edit / create dialog ─────────────────────────────── */}
      {editOpen !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditOpen(null)} data-testid="tax-cat-dialog">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editOpen.code ? 'Edit tax category' : 'Add tax category'}</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Code (e.g. VAT7_TH)</Label>
                <Input value={editOpen.code || ''} onChange={e => setEditOpen({ ...editOpen, code: e.target.value.toUpperCase() })} placeholder="VAT7_TH" data-testid="tax-cat-code-input" />
              </div>
              <div>
                <Label className="text-xs">Display name</Label>
                <Input value={editOpen.name || ''} onChange={e => setEditOpen({ ...editOpen, name: e.target.value })} placeholder="VAT 7% (Thailand)" data-testid="tax-cat-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Rate (%)</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={Number(editOpen.rate || 0) * 100} onChange={e => setEditOpen({ ...editOpen, rate: Number(e.target.value) / 100 })} data-testid="tax-cat-rate-input" />
                </div>
                <div>
                  <Label className="text-xs">Country (ISO)</Label>
                  <Input maxLength={2} value={editOpen.country || ''} onChange={e => setEditOpen({ ...editOpen, country: e.target.value.toUpperCase() })} placeholder="TH" data-testid="tax-cat-country-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <select value={editOpen.type || 'vat'} onChange={e => setEditOpen({ ...editOpen, type: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="tax-cat-type-select">
                  <option value="vat">VAT</option>
                  <option value="gst">GST</option>
                  <option value="sales_tax">Sales tax</option>
                  <option value="exempt">Exempt / Zero-rated</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setEditOpen(null)} data-testid="tax-cat-cancel-btn">Cancel</Button>
              <Button onClick={saveCat} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tax-cat-save-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
